"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const Spinner_1 = __importDefault(require("../Spinner/Spinner"));
const styled_1 = __importDefault(require("../utils/styled"));
const utils_1 = require("../utils");
const useUniqueId_1 = require("../useUniqueId");
const TabsBar = styled_1.default("div")(({ theme }) => ({
    display: "flex",
    alignItems: "flex-end",
    height: theme.tabsBarHeight,
    position: "relative",
    // This draws the line underneath the tabs
    "&::before": {
        content: "''",
        display: "block",
        position: "absolute",
        width: "100%",
        height: 0,
        top: theme.tabsBarHeight - 1,
        left: 0,
        borderBottom: `1px solid ${theme.color.border.select}`,
    },
}));
const TabContainer = styled_1.default("div")(({ theme, active }) => (Object.assign({ width: 150, height: "100%", display: "flex", alignItems: "center", fontFamily: theme.font.family.main, fontSize: theme.font.size.small, padding: `0px ${theme.space.element}px`, border: `1px solid ${theme.color.border.select}`, zIndex: 2 }, (active
    ? {
        backgroundColor: theme.color.white,
        color: theme.color.text.action,
        fontWeight: theme.font.weight.bold,
        borderBottomColor: theme.color.white,
    }
    : {
        backgroundColor: theme.color.background.lighter,
        color: theme.color.text.dark,
        fontWeight: theme.font.weight.regular,
        borderBottomColor: theme.color.border.select,
    }), { borderRightWidth: 0, ":last-child": {
        borderRightWidth: 1,
    }, ":hover": {
        cursor: "pointer",
        backgroundColor: active ? theme.color.white : utils_1.lighten(theme.color.background.lighter, 3),
    }, ":focus": Object.assign({}, utils_1.inputFocus({ theme, isError: false }), { borderRightWidth: 1, zIndex: 3 }) })));
const TabName = styled_1.default("p")({
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "center",
});
const getTabIndexByName = (tabs, tabName) => {
    if (tabName) {
        const index = tabs.findIndex(({ name }) => name === tabName);
        return index === -1 ? 0 : index;
    }
    return 0;
};
const SingleTab = ({ id, index, isActive, onTabClick, tab, isKeyboardActive }) => {
    const $container = react_1.default.useRef(null);
    react_1.useEffect(() => {
        if (isActive && isKeyboardActive && $container.current) {
            $container.current.focus();
        }
    }, [isKeyboardActive, isActive]);
    return (react_1.default.createElement(TabContainer, { ref: $container, role: "tab", active: isActive, "aria-selected": isActive, tabIndex: isActive ? 0 : -1, onClick: () => onTabClick(index), onMouseDown: e => e.preventDefault() },
        tab.loading ? (react_1.default.createElement(Spinner_1.default, { left: true, size: 14 })) : (tab.icon && react_1.default.createElement(tab.icon, { size: 14, color: tab.iconColor, left: true })),
        react_1.default.createElement(TabName, { id: id, "aria-label": tab.name }, tab.name)));
};
const Tabs = ({ onTabChange, tabs, activeTabName, children }) => {
    const activeTabIndex = getTabIndexByName(tabs, activeTabName);
    const [activeTab, setActiveTab] = react_1.useState(activeTabIndex);
    const isKeyboardActive = react_1.useRef(false);
    const uniqueId = useUniqueId_1.useUniqueId();
    const getUniqueTabId = react_1.default.useCallback((index) => `operational-ui__Tab--${uniqueId}-${index}`, [uniqueId]);
    const tabsWithId = react_1.default.useMemo(() => tabs.map((tab, index) => (Object.assign({}, tab, { id: tab.id || getUniqueTabId(index) }))), [
        tabs,
        getUniqueTabId,
    ]);
    react_1.useEffect(() => {
        setActiveTab(activeTabIndex);
    }, [activeTabIndex]);
    const onTabClick = react_1.useCallback((index) => {
        setActiveTab(index);
        if (onTabChange) {
            onTabChange(tabs[index].name);
        }
    }, [onTabChange, tabs, setActiveTab]);
    const onKeyDown = react_1.useCallback((event) => {
        isKeyboardActive.current = true;
        switch (event.key) {
            case "ArrowRight":
                event.preventDefault();
                onTabClick(activeTab + 1 >= tabs.length ? 0 : activeTab + 1);
                break;
            case "ArrowLeft":
                event.preventDefault();
                onTabClick(activeTab - 1 < 0 ? tabs.length - 1 : activeTab - 1);
                break;
            case "Home":
                event.preventDefault();
                // Activate first tab
                onTabClick(0);
                break;
            case "End":
                event.preventDefault();
                // Activate last tab
                onTabClick(tabs.length - 1);
                break;
        }
    }, [activeTab, onTabClick]);
    if (tabsWithId.length === 0 || tabs.length === 0) {
        return null;
    }
    // Work around: wrap return in fragment- to prevent type error and not having to change childrens return type
    // https://github.com/Microsoft/TypeScript/issues/21699
    return (react_1.default.createElement(react_1.default.Fragment, null, children({
        tabsBar: (react_1.default.createElement(TabsBar, { role: "tablist", onKeyDown: onKeyDown, onMouseDown: () => (isKeyboardActive.current = false) }, tabs
            .filter(({ hidden }) => !hidden)
            .map((tab, index) => (react_1.default.createElement(SingleTab, { id: tab.id || getUniqueTabId(index), key: index, index: index, tab: tab, onTabClick: onTabClick, isActive: activeTab === index, isKeyboardActive: isKeyboardActive.current }))))),
        activeTabId: (tabsWithId[activeTab] || tabsWithId[0]).id,
        activeChildren: (tabs[activeTab] || tabs[0]).children,
    })));
};
exports.default = Tabs;
//# sourceMappingURL=Tabs.js.map